import React from "react";
import { Tag, TagProps } from "antd";
import moment from "moment";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";

import { InstitutionCell } from "./InstitutionCell";
import type { Mandate } from "@/providers/smarta-banking/banking-api-client";
import { Label, Tbody, Td, Tr, Value } from "../../../components/styles";
import { DISPLAY_DATE_TIME_FORMAT } from "@/v1/constants";

const getTexts = (t: TFunction) => ({
  labels: {
    createdAt: t("mandateTable.labels.createdAt"),
    updatedAt: t("mandateTable.labels.updatedAt"),
  },
  states: {
    enqueued: t("mandateTable.states.enqueued"),
    pending: t("mandateTable.states.pending"),
    processing: t("mandateTable.states.processing"),
    accepted: t("mandateTable.states.accepted"),
    activated: t("mandateTable.states.activated"),
    timeout: t("mandateTable.states.timeout"),
    errored: t("mandateTable.states.errored"),
    renewal: t("mandateTable.states.renewal"),
  },
});

export type EnqueuedMandate = Omit<Mandate, "state"> & {
  state: "enqueued";
};
export type MandateTableData = (Mandate | EnqueuedMandate) & {
  type: "mandate";
};

type Props = {
  data: MandateTableData;
};

const stateToTagColor: Record<
  (Mandate | EnqueuedMandate)["state"],
  TagProps["color"]
> = {
  enqueued: "cyan",
  pending: "gold",
  processing: "orange",
  accepted: "lime",
  activated: "green",
  timeout: "magenta",
  errored: "red",
  renewal: "blue",
};

export const MandateTable: React.FC<Props> = ({ data }) => {
  const { t } = useTranslation("Banking");
  const texts = getTexts(t);
  const { institutionId, state, createdAt, updatedAt } = data;
  return (
    <Tbody>
      <Tr isHeaderRow>
        <InstitutionCell institutionId={institutionId} />

        <Td>
          <Label>{texts.labels.createdAt}</Label>
          <Value>{moment(createdAt).format(DISPLAY_DATE_TIME_FORMAT)}</Value>
        </Td>
        <Td>
          <Label>{texts.labels.updatedAt}</Label>
          <Value>{moment(updatedAt).format(DISPLAY_DATE_TIME_FORMAT)}</Value>
        </Td>
        <Td />
        <Td className="text-right">
          <Tag color={stateToTagColor[state]}>{texts.states[state]}</Tag>
        </Td>
        <Td />
      </Tr>
    </Tbody>
  );
};
